import { uid } from "@/lib/uid.ts";
import { Activity, Brain, CalendarClock, FolderTree, GitBranch, Globe, Sparkles, StickyNote, TerminalSquare, Users, X, ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BrowserPane } from "@/features/browser/BrowserPane.tsx";
import { cn } from "@/lib/cn.ts";
import { useDevice } from "@/lib/media.ts";
import { Terminal } from "@/ui/Terminal.tsx";
import { FilesPanel } from "./FilesPanel.tsx";
import { ServicesPanel } from "./ServicesPanel.tsx";
import { GitPanel } from "./GitPanel.tsx";
import { NotePanel } from "./NotePanel.tsx";
import { MemoryPanel } from "./MemoryPanel.tsx";
import { ChatMemoryPanel } from "./ChatMemoryPanel.tsx";
import { useParams } from "@tanstack/react-router";
import { SchedulesPanel } from "./SchedulesPanel.tsx";
import { TeamPanel } from "./TeamPanel.tsx";
import { ProvidersPanel } from "@/features/space/providers.tsx";
import { useQuery } from "@tanstack/react-query";
import { projectQuery } from "@/features/projects/ProjectPage.tsx";

export type DockTenant = "team" | "providers" | "browser" | "terminal" | "files" | "git" | "note" | "memory" | "schedules" | "services";
export const DOCK_TENANTS: DockTenant[] = ["team", "providers", "browser", "terminal", "files", "git", "note", "memory", "schedules", "services"];
const WIDTH_KEY = "codvero.dock.width";

const TABLET_WIDTH_KEY = "codvero.dock.width.tablet";
/** Panels open narrow; a width the user drags to is remembered (per desktop / iPad). */
function readWidth(key: string, fallback: number) {
  try {
    const v = Number(localStorage.getItem(key));
    return v >= 300 && v <= 1100 ? v : fallback;
  } catch {
    return fallback;
  }
}

/** Right-side (start-side in RTL is still "end") tool dock: one tenant at a time, draggable width, full-screen on small screens. */
/** Terminal session id survives reloads (sessionStorage) so a dropped tab reattaches to the same shell. */
function termSessionFor(projectId: string, fresh = false): string {
  const key = `codvero.term.${projectId}`;
  try {
    const cur = sessionStorage.getItem(key);
    if (cur && !fresh) return cur;
    const id = uid();
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return uid();
  }
}

export function Dock({ projectId, tenant, canEdit, running, onClose, onCapture }: { projectId: string; tenant: DockTenant; canEdit: boolean; running: boolean; onClose: () => void; onCapture?: (block: string) => void }) {
  const { t } = useTranslation();
  const device = useDevice();
  const [width, setWidth] = useState(() => readWidth(WIDTH_KEY, 440));
  // iPad keeps its own width: the panel floats over the chat, so it starts compact
  const [tabletWidth, setTabletWidth] = useState(() => readWidth(TABLET_WIDTH_KEY, 380));
  const latest = useRef({ width, tabletWidth });
  latest.current = { width, tabletWidth };
  const deviceRef = useRef(device);
  deviceRef.current = device;
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);
  const [termSession, setTermSession] = useState(() => termSessionFor(projectId));

  const onMove = useCallback((e: PointerEvent) => {
    if (!dragging.current) return;
    const rtl = document.documentElement.dir === "rtl";
    const delta = (e.clientX - startX.current) * (rtl ? 1 : -1);
    const next = Math.min(Math.round(window.innerWidth * 0.9), 1100, Math.max(300, startW.current + delta));
    if (deviceRef.current === "tablet") { latest.current.tabletWidth = next; setTabletWidth(next); }
    else { latest.current.width = next; setWidth(next); }
  }, []);
  const onUp = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    document.body.style.userSelect = "";
    const tablet = deviceRef.current === "tablet";
    try {
      localStorage.setItem(tablet ? TABLET_WIDTH_KEY : WIDTH_KEY, String(Math.round(tablet ? latest.current.tabletWidth : latest.current.width)));
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [onMove, onUp]);

  // the Chat section's space: its tasks ask providers (picked per task)
  const personal = useQuery({ ...projectQuery(projectId), staleTime: 60_000 }).data?.kind === "personal";
  const openChatId = (useParams({ strict: false }) as { chatId?: string }).chatId ?? null;
  const icons: Record<DockTenant, typeof Globe> = { team: Users, providers: Sparkles, browser: Globe, terminal: TerminalSquare, files: FolderTree, git: GitBranch, note: StickyNote, memory: Brain, schedules: CalendarClock, services: Activity };
  const Icon = icons[tenant];
  let body: ReactNode;
  if (tenant === "team") body = <TeamPanel projectId={projectId} />;
  else if (tenant === "providers") body = <ProvidersPanel />;
  else if (tenant === "note") body = <NotePanel projectId={projectId} />;
  else if (tenant === "schedules") body = <SchedulesPanel projectId={projectId} canEdit={canEdit} chatSection={personal} />;
  else if (!running) body = <div className="grid h-full place-items-center p-6 text-center text-sm text-fg-muted">{t("errors.project_not_running")}</div>;
  else if (tenant === "browser") body = <BrowserPane projectId={projectId} canEdit={canEdit} className="h-full rounded-none border-0" {...(onCapture ? { onCapture } : {})} />;
  else if (tenant === "terminal")
    body = canEdit ? (
      <div className="flex h-full flex-col">
        <div className="flex justify-end border-b border-border px-2 py-1"><button type="button" className="text-xs text-fg-muted hover:text-fg" onClick={() => setTermSession(termSessionFor(projectId, true))}>{t("terminal.newSession")}</button></div>
        <Terminal key={termSession} target={`project:${projectId}`} session={termSession} className="min-h-0 flex-1 bg-[#15171c] p-1" />
      </div>
    ) : <div className="p-6 text-sm text-fg-muted">{t("errors.forbidden")}</div>;
  else if (tenant === "files") body = <FilesPanel projectId={projectId} />;
  else if (tenant === "services") body = <ServicesPanel projectId={projectId} canEdit={canEdit} running={running} />;
  else if (tenant === "git") body = <GitPanel projectId={projectId} canEdit={canEdit} />;
  // the Chat section: the open conversation's memories (each conversation and space has its own)
  else if (tenant === "memory") body = personal && openChatId ? <ChatMemoryPanel chatId={openChatId} projectId={projectId} canEdit={canEdit} /> : <MemoryPanel projectId={projectId} canEdit={canEdit} chatSection={personal} />;
  else body = <div className="grid h-full place-items-center p-6 text-center text-sm text-fg-muted">{t("dock.soon", { name: t(`dock.${tenant as string}`) })}</div>;

  return (
    <>
    {/* iPad: the panel floats over the chat; tapping the dimmed chat closes it */}
    {device === "tablet" && <button type="button" aria-label={t("common.close")} className="fixed inset-0 z-20 bg-black/30" onClick={onClose} />}
    <aside
      className={cn(
        "flex flex-col bg-surface",
        device === "phone" && "fixed inset-0 z-50 pt-safe pb-safe",
        device === "tablet" && "fixed inset-y-0 end-0 z-30 border-s border-border shadow-2xl pt-safe",
        device === "desktop" && "relative z-10 h-full shrink-0 border-s border-border",
      )}
      style={{ width: device === "desktop" ? width : device === "tablet" ? Math.min(tabletWidth, Math.round(window.innerWidth * 0.9)) : undefined }}
    >
      <div className="relative flex h-full flex-col">
        <div
          role="separator"
          aria-orientation="vertical"
          className={cn("group absolute inset-y-0 start-0 z-10 w-1.5 cursor-col-resize touch-none hover:bg-accent/40 [@media(pointer:coarse)]:w-4", device === "phone" ? "hidden" : "block")}
          onPointerDown={(e) => {
            dragging.current = true;
            startX.current = e.clientX;
            startW.current = device === "tablet" ? tabletWidth : width;
            document.body.style.userSelect = "none";
          }}
        >
          <span className="pointer-events-none absolute start-1 top-1/2 hidden h-10 w-1 -translate-y-1/2 rounded-full bg-fg-muted/50 [@media(pointer:coarse)]:block" />
        </div>
        <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            {/* phone: a back arrow to the chat (the panel covers the whole screen) */}
            {device === "phone" && <button type="button" aria-label={t("common.back")} onClick={onClose} className="tap -ms-1 grid place-items-center rounded-md text-fg-muted hover:text-fg"><ArrowLeft className="size-5 rtl:-scale-x-100" /></button>}
            <Icon className="size-4 text-accent" /> {t(`dock.${tenant}`)}
          </div>
          {device !== "phone" && <button type="button" aria-label={t("common.close")} onClick={onClose} className="tap grid place-items-center rounded p-1 text-fg-muted hover:bg-surface-2 hover:text-fg"><X className="size-4" /></button>}
        </header>
        <div className={cn("min-h-0 flex-1", tenant === "browser" && "overflow-hidden")}>{body}</div>
      </div>
    </aside>
    </>
  );
}


